/**
 * Utility functions for handling rich text content with proper styling
 */

/**
 * Processes rich text content to ensure proper image/video sizing and word wrapping
 * @param {string} htmlContent - The HTML content from rich text editor
 * @param {Object} options - Configuration options
 * @param {string} options.imageMaxWidth - Maximum width for images (default: '100%')
 * @param {string} options.imageMaxHeight - Maximum height for images (default: '300px')
 * @param {string} options.videoMaxWidth - Maximum width for videos (default: '100%')
 * @param {string} options.videoMaxHeight - Maximum height for videos (default: '300px')
 * @param {string} options.iframeMaxWidth - Maximum width for iframes (default: '100%')
 * @param {string} options.iframeMaxHeight - Maximum height for iframes (default: '300px')
 * @param {boolean} options.showImages - Whether to show images (default: true)
 * @param {boolean} options.showVideos - Whether to show videos (default: true)
 * @param {boolean} options.showIframes - Whether to show iframes (default: true)
 * @param {string} options.containerMaxWidth - Maximum width of the container (default: '100%')
 * @returns {string} - Processed HTML content with proper styling
 */
export const processRichTextContent = (htmlContent, options = {}) => {
  if (!htmlContent) return '';

  const {
    imageMaxWidth = '100%',
    imageMaxHeight = '300px',
    videoMaxWidth = '100%',
    videoMaxHeight = '300px',
    iframeMaxWidth = '100%',
    iframeMaxHeight = '300px',
    showImages = true,
    showVideos = true,
    showIframes = true,
    containerMaxWidth = '100%'
  } = options;

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Process images
  if (showImages) {
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => {
      img.style.maxWidth = imageMaxWidth;
      img.style.maxHeight = imageMaxHeight;
      img.style.width = 'auto';
      img.style.height = 'auto';
      img.style.borderRadius = '8px';
      img.style.margin = '8px 0';
      img.style.display = 'block';
      img.style.objectFit = 'contain';
      img.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    });
  } else {
    // Remove images if not showing
    const images = tempDiv.querySelectorAll('img');
    images.forEach(img => img.remove());
  }

  // Process videos
  if (showVideos) {
    const videos = tempDiv.querySelectorAll('video');
    videos.forEach(video => {
      video.style.maxWidth = videoMaxWidth;
      video.style.maxHeight = videoMaxHeight;
      video.style.width = 'auto';
      video.style.height = 'auto';
      video.style.borderRadius = '8px';
      video.style.margin = '8px 0';
      video.style.display = 'block';
      video.style.objectFit = 'contain';
      video.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    });
  } else {
    // Remove videos if not showing
    const videos = tempDiv.querySelectorAll('video');
    videos.forEach(video => video.remove());
  }

  // Process iframes (embeds)
  if (showIframes) {
    const iframes = tempDiv.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      iframe.style.maxWidth = iframeMaxWidth;
      iframe.style.maxHeight = iframeMaxHeight;
      iframe.style.width = '100%';
      iframe.style.height = 'auto';
      iframe.style.aspectRatio = '16/9';
      iframe.style.borderRadius = '8px';
      iframe.style.border = 'none';
      iframe.style.margin = '8px 0';
      iframe.style.display = 'block';
      iframe.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
    });
  } else {
    // Remove iframes if not showing
    const iframes = tempDiv.querySelectorAll('iframe');
    iframes.forEach(iframe => iframe.remove());
  }

  // Process paragraphs for word wrapping
  const paragraphs = tempDiv.querySelectorAll('p');
  paragraphs.forEach(p => {
    p.style.margin = '8px 0';
    p.style.wordBreak = 'break-word';
    p.style.overflowWrap = 'break-word';
    p.style.hyphens = 'auto';
    p.style.lineHeight = '1.6';
  });

  // Process headings
  const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach(heading => {
    heading.style.margin = '12px 0 8px 0';
    heading.style.wordBreak = 'break-word';
    heading.style.overflowWrap = 'break-word';
  });

  // Process links
  const links = tempDiv.querySelectorAll('a');
  links.forEach(link => {
    link.style.color = '#1976d2';
    link.style.textDecoration = 'none';
    link.style.wordBreak = 'break-all';
    link.style.overflowWrap = 'break-word';
    link.addEventListener('mouseenter', () => {
      link.style.textDecoration = 'underline';
    });
    link.addEventListener('mouseleave', () => {
      link.style.textDecoration = 'none';
    });
  });

  // Process tables
  const tables = tempDiv.querySelectorAll('table');
  tables.forEach(table => {
    table.style.borderCollapse = 'collapse';
    table.style.margin = '12px 0';
    table.style.width = '100%';
    table.style.maxWidth = containerMaxWidth;
    table.style.overflowX = 'auto';
    table.style.display = 'block';
    
    const cells = table.querySelectorAll('th, td');
    cells.forEach(cell => {
      cell.style.border = '1px solid #ddd';
      cell.style.padding = '8px 12px';
      cell.style.fontSize = '14px';
      cell.style.wordBreak = 'break-word';
      cell.style.overflowWrap = 'break-word';
    });
  });

  // Process lists
  const lists = tempDiv.querySelectorAll('ul, ol');
  lists.forEach(list => {
    list.style.margin = '8px 0';
    list.style.paddingLeft = '20px';
    
    const listItems = list.querySelectorAll('li');
    listItems.forEach(item => {
      item.style.margin = '4px 0';
      item.style.wordBreak = 'break-word';
      item.style.overflowWrap = 'break-word';
    });
  });

  // Process blockquotes
  const blockquotes = tempDiv.querySelectorAll('blockquote');
  blockquotes.forEach(blockquote => {
    blockquote.style.margin = '12px 0';
    blockquote.style.padding = '12px 16px';
    blockquote.style.borderLeft = '4px solid #1976d2';
    blockquote.style.backgroundColor = '#f5f5f5';
    blockquote.style.borderRadius = '4px';
    blockquote.style.wordBreak = 'break-word';
    blockquote.style.overflowWrap = 'break-word';
  });

  // Process code blocks
  const codeBlocks = tempDiv.querySelectorAll('pre');
  codeBlocks.forEach(pre => {
    pre.style.margin = '12px 0';
    pre.style.padding = '12px';
    pre.style.backgroundColor = '#f5f5f5';
    pre.style.borderRadius = '4px';
    pre.style.overflowX = 'auto';
    pre.style.wordBreak = 'break-all';
    pre.style.whiteSpace = 'pre-wrap';
  });

  // Process inline code
  const inlineCode = tempDiv.querySelectorAll('code');
  inlineCode.forEach(code => {
    code.style.backgroundColor = '#f5f5f5';
    code.style.padding = '2px 4px';
    code.style.borderRadius = '3px';
    code.style.fontFamily = 'monospace';
    code.style.wordBreak = 'break-all';
  });

  return tempDiv.innerHTML;
};

/**
 * Creates a sanitized and styled rich text content with default settings
 * @param {string} htmlContent - The HTML content from rich text editor
 * @returns {string} - Processed HTML content with default styling
 */
export const sanitizeRichText = (htmlContent) => {
  return processRichTextContent(htmlContent);
};

/**
 * Creates a compact version of rich text content for previews
 * @param {string} htmlContent - The HTML content from rich text editor
 * @param {number} maxLength - Maximum text length (default: 150)
 * @returns {string} - Processed HTML content for preview
 */
export const createRichTextPreview = (htmlContent, maxLength = 150) => {
  if (!htmlContent) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  if (textContent.length <= maxLength) {
    return processRichTextContent(htmlContent, {
      imageMaxHeight: '150px',
      videoMaxHeight: '150px',
      iframeMaxHeight: '150px'
    });
  }

  return textContent.substring(0, maxLength) + '...';
};

/**
 * Extracts plain text from rich text content
 * @param {string} htmlContent - The HTML content from rich text editor
 * @returns {string} - Plain text content
 */
export const extractTextFromRichText = (htmlContent) => {
  if (!htmlContent) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Checks if rich text content contains media (images, videos, iframes)
 * @param {string} htmlContent - The HTML content from rich text editor
 * @returns {Object} - Object with boolean flags for different media types
 */
export const hasMediaContent = (htmlContent) => {
  if (!htmlContent) return { hasImages: false, hasVideos: false, hasIframes: false };

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  return {
    hasImages: tempDiv.querySelectorAll('img').length > 0,
    hasVideos: tempDiv.querySelectorAll('video').length > 0,
    hasIframes: tempDiv.querySelectorAll('iframe').length > 0
  };
};
